import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { DietProvider } from "./context/DietContext";
import { ReactNode, useState, useEffect } from "react";
import Home from "./components/home/Home";
import LoginRegister from "./components/auth/LoginRegister";
import Dashboard from "./components/dashboard/Dashboard";
import DietPlanDetail from "./components/diet/DietPlanDetail";
import Appointments from "./components/appointment/Appointments";
import DietitianLayout from "./components/dietitian/DietitianLayout";
import DietitianDashboard from "./components/dietitian/DietitianDashboard";
import ClientList from "./components/dietitian/ClientList";
import ClientDetail from "./components/dietitian/ClientDetail";
import Schedule from "./components/dietitian/Schedule";
import FoodLibrary from "./components/dietitian/FoodLibrary";
import Progress from "./components/progress/Progress";
import loader_logo from "./assets/trophiaLogo.png";

// ── Full-page loader while Firebase restores session ──────
const AppLoader = ({ children }: { children: ReactNode }) => {
  const { loading } = useAuth();
  const [visible,  setVisible]  = useState(true);
  const [exiting,  setExiting]  = useState(false);

  useEffect(() => {
    if (!loading) {
      setExiting(true);
      const t = setTimeout(() => setVisible(false), 800);
      return () => clearTimeout(t);
    }
  }, [loading]);

  return (
    <>
      {visible && (
        <div style={{
          position: "fixed", inset: 0, display: "flex",
          alignItems: "center", justifyContent: "center",
          background: "#F7FAF7", zIndex: 9999,
          animation: exiting ? "overlayFade 0.8s ease 0.2s forwards" : undefined,
        }}>
          <img
            src={loader_logo}
            style={{
              height: 100,
              filter: "invert(42%) sepia(36%) saturate(500%) hue-rotate(75deg) brightness(90%) contrast(85%)",
              animation: exiting
                ? "logoFlyUp 0.55s cubic-bezier(0.4, 0, 0.2, 1) forwards"
                : "logoBreathe 2s ease-in-out infinite",
            }}
            alt="Loading"
          />
          <style>{`
            @keyframes logoBreathe {
              0%, 100% { opacity: 1;   transform: scale(1);    }
              50%       { opacity: 0.4; transform: scale(0.93); }
            }
            @keyframes logoFlyUp {
              0%   { opacity: 1; transform: scale(1)    translateY(0);     }
              100% { opacity: 0; transform: scale(1.08) translateY(-90px); }
            }
            @keyframes overlayFade {
              from { opacity: 1; }
              to   { opacity: 0; }
            }
          `}</style>
        </div>
      )}
      {children}
    </>
  );
};

// ── Redirect to home if not logged in, otherwise to correct dashboard ──
const RootRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Home />;
  return <Navigate to={user.role === "dietitian" ? "/dietitian" : "/dashboard"} replace />;
};

// ── Requires any logged-in user ───────────────────────────
const ProtectedRoute = ({ children }: { children: ReactNode }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <>{children}</> : <Navigate to="/" replace />;
};

// ── Requires dietitian role ───────────────────────────────
const RequireDietitian = ({ children }: { children: ReactNode }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/" replace />;
  if (user.role !== "dietitian") return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

function App() {
  return (
    <AuthProvider>
    <DietProvider>
      <AppLoader>
      <Routes>
        {/* Root: auto-redirect logged-in users, show landing page otherwise */}
        <Route path="/" element={<RootRedirect />} />
        <Route path="/login" element={<LoginRegister />} />

        {/* Client routes */}
        <Route path="/dashboard"   element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/diet-plans"  element={<ProtectedRoute><DietPlanDetail /></ProtectedRoute>} />
        <Route path="/appointments" element={<ProtectedRoute><Appointments /></ProtectedRoute>} />
        <Route path="/progress"    element={<ProtectedRoute><Progress /></ProtectedRoute>} />

        {/* Dietitian-only routes */}
        <Route path="/dietitian" element={<RequireDietitian><DietitianLayout /></RequireDietitian>}>
          <Route index              element={<DietitianDashboard />} />
          <Route path="clients"     element={<ClientList />} />
          <Route path="clients/:id" element={<ClientDetail />} />
          <Route path="schedule"    element={<Schedule />} />
          <Route path="foods"       element={<FoodLibrary />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </AppLoader>
    </DietProvider>
    </AuthProvider>
  );
}

export default App;
